/*
 * AI智能对话题（ai_chat）答题组件
 * 对接后端接口：POST /api/v1/answer/test/aiChat/{surveyId}
 * 入参：header{id=sampleId}, body{questionId, text}
 * 出参：{reply, currentRound, totalRoundLimit, isFinished, messages:[{role,content}]}
 */
import React, {useState, useEffect, useRef, useCallback} from "react";
import axios from "../../utils/selfRequest";
import "./index.less";

const isMobile = /(iPhone|iPad|iPod|iOS|Android)/i.test(navigator.userAgent);

const normalizeSavedAiChatAnswer = (value) => {
    const data = typeof value === "string" ? JSON.parse(value) : value;
    if (Array.isArray(data)) {
        return {messages: data, isFinished: false};
    }
    if (data && typeof data === "object") {
        return {
            messages: Array.isArray(data.messages) ? data.messages : [],
            isFinished: !!(data.isFinished || data.completed),
        };
    }
    return {messages: [], isFinished: false};
};

const MAX_INPUT_LENGTH = 1000; // 用户最大输入字数

const AiChat = (props) => {
    const {info = {}, surveyId, sampleId, answer, changeAnswer} = props;
    const aiPerfer = info.aiPerfer || info.preferences?.aiPerfer || {};
    const {
        normalClosing = "感谢您的分享。",
    } = aiPerfer;

    const [messages, setMessages] = useState([]); // {role:'ai'|'user', text}
    const [input, setInput] = useState("");
    const [typing, setTyping] = useState(false);
    const [ended, setEnded] = useState(false);
    const [round, setRound] = useState(0);
    const [totalRounds, setTotalRounds] = useState(aiPerfer.totalRoundLimit || 12);

    // 输入是否超过千字限制
    const isOverLimit = input.length > MAX_INPUT_LENGTH;

    const msgRef = useRef(null);
    const inputRef = useRef(null);
    const timersRef = useRef([]);
    const initRef = useRef(false);
    const hasRestoredRef = useRef(false);

    // 提交答案给父组件（answers[index] = {info.id: value}）
    const submitAnswer = useCallback(
        (msgs, isEnded) => {
            if (!changeAnswer) return;
            const optionId = info.options && info.options[0] ? String(info.options[0].id) : "AI_OPTION";
            const data = {
                messages: msgs,
                isFinished: !!isEnded,
                completed: !!isEnded,
                round: msgs.filter((m) => m.role === "user").length,
            };
            changeAnswer({[optionId]: {"text": JSON.stringify(data)}});
        },
        [changeAnswer, info.options]
    );

    // 滚动到底部
    const scrollToBottom = useCallback(() => {
        if (msgRef.current) {
            msgRef.current.scrollTop = msgRef.current.scrollHeight;
        }
    }, []);

    // 手机端输入框聚焦（键盘弹出）时滚动到底部，避免键盘遮挡最新消息
    const handleInputFocus = useCallback(() => {
        if (!isMobile) return;
        // 键盘弹出有动画过程，分多次延迟确保滚到底部
        const t1 = setTimeout(scrollToBottom, 300);
        const t2 = setTimeout(scrollToBottom, 600);
        timersRef.current.push(t1, t2);
    }, [scrollToBottom]);

    // 手机端监听键盘弹出（视口高度变化），自动滚动到底部
    useEffect(() => {
        if (!isMobile) return;
        const handler = () => scrollToBottom();
        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", handler);
            return () => window.visualViewport.removeEventListener("resize", handler);
        }
        window.addEventListener("resize", handler);
        return () => window.removeEventListener("resize", handler);
    }, [scrollToBottom]);

    useEffect(() => {
        scrollToBottom();
    }, [messages, typing, scrollToBottom]);

    // 从 answer 回填 messages 到对话界面；answer 变化时同步
    useEffect(() => {
        try {
            if (answer) {
                // answer 结构: { questionId: { optionId: { text: "..." } } }
                // 遍历查找含 text 的条目，避免大数字 ID 精度丢失导致 key 不匹配
                for (const qKey of Object.keys(answer)) {
                    const qVal = answer[qKey];
                    if (qVal && typeof qVal === 'object') {
                        for (const oKey of Object.keys(qVal)) {
                            const oVal = qVal[oKey];
                            if (oVal && oVal.text) {
                                const {messages: msgs, isFinished} = normalizeSavedAiChatAnswer(oVal.text);
                                if (Array.isArray(msgs) && msgs.length) {
                                    setMessages(msgs);
                                    setRound(msgs.filter((m) => m.role === "user").length);
                                    setEnded(isFinished);
                                    hasRestoredRef.current = true;
                                    return;
                                }
                            }
                        }
                    }
                }
            }
        } catch (e) {
            /* ignore */
        }
    }, [answer]);

    // answer 为空时直接调用 aiChat 接口获取开场白（只在挂载时执行一次）
    useEffect(() => {
        if (initRef.current) return;
        initRef.current = true;
        const t = setTimeout(() => {
            // 已有回填数据时不调用接口
            if (hasRestoredRef.current) return;
            // 首次调用 aiChat 接口获取开场白
            callAiChat("");
        }, 300);
        timersRef.current.push(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 清理定时器
    useEffect(() => {
        return () => {
            timersRef.current.forEach(clearTimeout);
            timersRef.current = [];
        };
    }, []);

    // 调用后端 AI 对话接口
    const callAiChat = async (text) => {
        setTyping(true);
        try {
            const res = await axios.post(
                `answer/test/aiChat/${surveyId}`,
                {questionId: info.id, text},
                "api/v1/",
                {id: sampleId}
            );
            // res 为 {code, msg, data:{...}}，selfRequest 返回整个 data 层
            const resData = res.data || {};
            const reply = resData.reply || "";
            const currentRound = resData.currentRound || 0;
            const totalRoundLimit = resData.totalRoundLimit || totalRounds;
            const isFinished = resData.isFinished || false;

            setTotalRounds(totalRoundLimit);
            setRound(currentRound);

            setMessages((prev) => {
                const next = [...prev, {role: "ai", text: reply}];
                submitAnswer(next, isFinished);
                return next;
            });

            if (isFinished) {
                setEnded(true);
            }
        } catch (err) {
            // 接口失败时用兜底话术回复
            setMessages((prev) => {
                const next = [...prev, {role: "ai", text: aiPerfer.fallbackScript || "抱歉，出错了，请稍后重试。"}];
                submitAnswer(next, false);
                return next;
            });
        } finally {
            setTyping(false);
            if (inputRef.current) inputRef.current.focus();
        }
    };

    const send = () => {
        if (ended || typing || isOverLimit) return;
        const txt = (input || "").trim();
        if (!txt) return;
        setInput("");
        setMessages((prev) => {
            const next = [...prev, {role: "user", text: txt}];
            submitAnswer(next, false);
            return next;
        });
        // 调用后端 AI 对话接口
        callAiChat(txt);
    };

    const onKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            send();
        }
    };

    return (
        <div className={isMobile ? "ai-chat-wrap h5" : "ai-chat-wrap pc"}>
            <div className="chat-topbar">
                <span className="ai-label">🤖 AI访谈员</span>
            </div>
            <div className="chat-messages" ref={msgRef}>
                {messages.map((m, i) => (
                    <div key={i} className={`bubble bubble-${m.role}`}>
                        {m.text}
                    </div>
                ))}
                {typing && (
                    <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                )}
            </div>
            {ended ? (
                <div className="chat-ended">对话已结束，请点击"下一页"继续</div>
            ) : (
                <>
                    <div className="chat-input-area">
                        <input
                            ref={inputRef}
                            className="chat-input"
                            type="text"
                            placeholder="输入您的回答..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onFocus={handleInputFocus}
                            onKeyDown={onKeyDown}
                            disabled={typing}
                        />
                        <button
                            className="chat-send"
                            onClick={send}
                            disabled={typing || !input.trim() || isOverLimit}
                        >
                            ➤
                        </button>
                    </div>
                    {isOverLimit && (
                        <div className="chat-input-tip">输入内容超过{MAX_INPUT_LENGTH}字，请精简后发送</div>
                    )}
                </>
            )}
        </div>
    );
};

export default AiChat;
